import { useEffect, useState } from "react";
import type { BattleManager } from "../../game/assets/pri_wqs/scripts/core/BattleManager";
import type {
  BattleRuntimeState,
  PropRuntimeEvent,
  PropRuntimeEventType,
} from "../../game/assets/pri_wqs/scripts/core/propMechanics/PropMechanicsTypes";
import { getCommissionRoundLimit } from "./flowUtils";
import { formatAnimatedMagic, useAnimatedNumber } from "./MagicGainEffects";
import {
  StatCard,
  StatGrid,
  StatLabel,
  StatValue,
} from "./styles";

interface StatsViewState {
  stageLabel: string;
  commissionIndex: number;
  roundIndex: number;
  roundLimit: number;
  currentRoundMagic: number;
  totalMagic: number;
}

const EMPTY_STATS: StatsViewState = {
  stageLabel: "未开始",
  commissionIndex: 0,
  roundIndex: 0,
  roundLimit: 0,
  currentRoundMagic: 0,
  totalMagic: 0,
};

const STATS_EVENTS: PropRuntimeEventType[] = [
  "gameStarted",
  "gameEnded",
  "commissionStarted",
  "commissionEnded",  "roundStarted",  "roundEnded",
  "magicChanged",
  "rarityWeightChanged",
  "permanentBoostRulesChanged",
  "commissionRoundLimitChanged",
  "propAcquired",
  "propRemoved",  "propEnteredBoard",
  "propLeftBoard",
];

export function StatsBar({ manager }: { manager?: BattleManager }) {
  const [stats, setStats] = useState<StatsViewState>(EMPTY_STATS);
  const animatedRoundMagic = useAnimatedNumber(stats.currentRoundMagic ?? 0);
  const animatedTotalMagic = useAnimatedNumber(stats.totalMagic ?? 0);

  useEffect(() => {
    if (!manager) {
      setStats(EMPTY_STATS);
      return undefined;
    }

    const refresh = (event?: PropRuntimeEvent) => {
      setStats((previous) => readStats(manager.getState(), event, previous.stageLabel));
    };

    refresh();
    const eventBus = manager.getEventBus();
    const unsubs = STATS_EVENTS.map((type) => eventBus.on(type, refresh));
    return () => {
      unsubs.forEach((unsubscribe) => unsubscribe());
    };
  }, [manager]);

  return (
    <StatGrid>
      <StatCard>
        <StatLabel>阶段</StatLabel>
        <StatValue>{stats.stageLabel}</StatValue>
      </StatCard>
      <StatCard>
        <StatLabel>当前委托</StatLabel>
        <StatValue>{stats.commissionIndex || 0} / 6</StatValue>
      </StatCard>
      <StatCard>
        <StatLabel>委托内轮次</StatLabel>
        <StatValue>{stats.roundIndex || 0} / {stats.roundLimit}</StatValue>
      </StatCard>
      <StatCard>
        <StatLabel>本轮魔力</StatLabel>
        <StatValue>{formatAnimatedMagic(animatedRoundMagic)}</StatValue>
      </StatCard>
      <StatCard>
        <StatLabel>总魔力</StatLabel>
        <StatValue>{formatAnimatedMagic(animatedTotalMagic)}</StatValue>
      </StatCard>
    </StatGrid>
  );
}

function readStats(
  state: BattleRuntimeState,
  event: PropRuntimeEvent | undefined,
  previousStageLabel: string,
): StatsViewState {
  return {
    stageLabel: event ? readStageLabel(event.type, previousStageLabel) : readInitialStageLabel(state),
    commissionIndex: state.commissionIndex,
    roundIndex: state.roundIndex,
    roundLimit: state.commissionIndex > 0
      ? getCommissionRoundLimit(state.commissionIndex, state.extraCommissionRounds)
      : 0,
    currentRoundMagic: state.currentRoundMagic,
    totalMagic: state.totalMagic,
  };
}

function readInitialStageLabel(state: BattleRuntimeState): string {
  if (state.commissionIndex > 0) return "委托中";
  return "未开始";
}

function readStageLabel(type: PropRuntimeEventType, fallback: string): string {
  switch (type) {
    case "gameStarted":
      return "游戏开始";
    case "gameEnded":
      return "游戏结束";
    case "commissionStarted":
      return "委托开始";
    case "commissionEnded":
      return "委托结束";
    case "roundStarted":
      return "回合开始";
    case "roundEnded":
      return "回合结束";
    default:
      return fallback || "运行中";
  }
}
